import { useCallback, useEffect, useState } from 'react';
import hubService, { Missao } from '../../services/hub.service';

const DIFIC_BG: Record<Missao['dificuldade'], string> = {
  facil: 'bg-green-100 text-green-800',
  media: 'bg-yellow-100 text-yellow-800',
  dificil: 'bg-red-100 text-red-800',
};

const ESTADO_BG: Record<Missao['estado'], string> = {
  em_progresso: 'bg-blue-50 border-blue-200',
  completa: 'bg-green-50 border-green-300 ring-2 ring-green-400',
  reclamada: 'bg-gray-100 border-gray-200 opacity-60',
  expirada: 'bg-gray-100 border-gray-200 opacity-50',
};

export default function Missions() {
  const [missoes, setMissoes] = useState<Missao[]>([]);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState<number | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setMissoes(await hubService.getMissoes());
    } catch {
      setMissoes([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const claim = async (m: Missao) => {
    setClaiming(m.id);
    try {
      const r = await hubService.reclamarMissao(m.id);
      setToast(`🎉 +${r.pontos_creditados} pontos${r.bonus_cve > 0 ? ` + ${r.bonus_cve} CVE` : ''}`);
      setTimeout(() => setToast(null), 3500);
      await load();
    } catch (e: any) {
      setToast(e?.message || 'Erro ao reclamar');
      setTimeout(() => setToast(null), 3000);
    } finally {
      setClaiming(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (missoes.length === 0) {
    return (
      <div className="text-center py-16 px-4">
        <div className="text-6xl mb-4">🎯</div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">Sem missões ativas</h3>
        <p className="text-gray-600 max-w-md mx-auto">
          Volta em breve — estamos a preparar desafios para tu ganhares mais pontos e subir de nível.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {toast && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 bg-green-600 text-white px-6 py-3 rounded-full shadow-lg font-medium animate-bounce">
          {toast}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {missoes.map((m) => {
          const pct = Math.min(100, Math.round((m.progresso_atual / m.objetivo) * 100));
          const canClaim = m.estado === 'completa';
          const isDone = m.estado === 'reclamada';

          return (
            <div
              key={m.id}
              className={`border-2 rounded-xl p-5 transition-all ${ESTADO_BG[m.estado]}`}
            >
              <div className="flex items-start gap-3 mb-3">
                <div className="text-4xl flex-shrink-0">{m.icone || '🎯'}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-bold text-gray-900">{m.titulo}</h3>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${DIFIC_BG[m.dificuldade]}`}>
                      {m.dificuldade}
                    </span>
                  </div>
                  {m.descricao && (
                    <p className="text-sm text-gray-600">{m.descricao}</p>
                  )}
                </div>
              </div>

              {/* Progress bar */}
              <div className="mb-3">
                <div className="flex justify-between items-center mb-1.5 text-sm">
                  <span className="font-medium text-gray-700">
                    {m.progresso_atual} / {m.objetivo}
                  </span>
                  <span className="text-xs text-gray-500">{pct}%</span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className={`h-full transition-all ${
                      canClaim ? 'bg-green-500' : isDone ? 'bg-gray-400' : 'bg-gradient-to-r from-purple-500 to-pink-500'
                    }`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>

              {/* Reward + action */}
              <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl">⭐</span>
                  <span className="text-lg font-bold text-purple-700">{m.pontos_recompensa}</span>
                  <span className="text-sm text-gray-600">pts</span>
                  {m.bonus_cve > 0 && (
                    <span className="text-sm text-amber-700 font-semibold ml-2">
                      +{m.bonus_cve} CVE
                    </span>
                  )}
                </div>
                {canClaim && (
                  <button
                    onClick={() => claim(m)}
                    disabled={claiming === m.id}
                    className="bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white font-bold px-4 py-2 rounded-lg transition text-sm"
                  >
                    {claiming === m.id ? 'A reclamar…' : 'Reclamar 🎁'}
                  </button>
                )}
                {isDone && (
                  <span className="text-green-700 font-semibold text-sm">✓ Reclamada</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
